import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Tabs, Tab } from "react-bootstrap";
import EventShareButtons from "../components/EventShareButtons";
import { fetchGroupById, fetchGroupByCode } from "../services/GroupService";
import { fetchProgramsByGroupPaginated } from "../services/ProgramService";
import { getStateNameByCode } from "../services/LocationService";
import { apiGet } from "../services/BaseService";
import GroupData from "../models/GroupData";
import { IMAGE_BASE } from "../Config";
import { getGroupImageUrl, getPlayImageUrl, handleImageError } from "../utils/imageUtils";

const GroupDetailPage: React.FC = () => {
  const { id, groupCode } = useParams<{ id?: string; groupCode?: string }>();
  const [group, setGroup] = useState<GroupData | null>(null);
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateName, setStateName] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalPrograms, setTotalPrograms] = useState(0);
  const [programTypes, setProgramTypes] = useState<any[]>([]);
  const [programsByType, setProgramsByType] = useState<{[key: string]: any[]}>({});
  const [festivalPlays, setFestivalPlays] = useState<{[key: string]: any[]}>({});
  const [activeTab, setActiveTab] = useState<string>("plays");

  useEffect(() => {
    const fetchData = async () => {
      if (!id && !groupCode) return;
      
      try {
        setLoading(true);
        let groupData;
        
        // Fetch group by ID or groupCode
        if (groupCode) {
          groupData = await fetchGroupByCode(groupCode);
        } else if (id) {
          groupData = await fetchGroupById(id);
        }
        else{
          throw new Error("No valid group identifier provided");
        }
        
        // Fetch programs using the group ID with pagination
        const groupId = groupData._id;
        const programsData = await fetchProgramsByGroupPaginated(groupId, 1, 50);
        
        // Fetch program types
        const typesData = await apiGet('/programtypes');
        
        setGroup(groupData);
        setPrograms(programsData.programs || []);
        setHasMore(programsData.hasMore || false);
        setTotalPrograms(programsData.totalPrograms || 0);
        setCurrentPage(1);
        setProgramTypes(typesData);
        
        // Group programs by type
        const groupedPrograms: {[key: string]: any[]} = {};
        const groupedFestivalPlays: {[key: string]: any[]} = {};
        const allPrograms = programsData.programs || [];
        
        // Theater plays (default)
        groupedPrograms['plays'] = allPrograms.filter((p: any) => p.programType === '6839c953077dbaaa4e2581da');
        
        // Other program types and fetch festival plays for festivals
        for (const type of typesData) {
          if (type._id !== '6839c953077dbaaa4e2581da') {
            const typePrograms = allPrograms.filter((p: any) => p.programType === type._id);
            groupedPrograms[type._id] = typePrograms;
            
            // If it's a festival type, fetch festival plays for each festival
            if (type.name.toLowerCase().includes('festival')) {
              for (const festival of typePrograms) {
                try {
                  const festivalPlaysData = await apiGet(`/festival-plays/festival/${festival._id}`);
                  groupedFestivalPlays[festival._id] = festivalPlaysData;
                } catch (error) {
                  console.error(`Failed to fetch festival plays for ${festival.title}:`, error);
                  groupedFestivalPlays[festival._id] = [];
                }
              }
            }
          }
        }
        
        setProgramsByType(groupedPrograms);
        setFestivalPlays(groupedFestivalPlays);
        
        // Load state name
        if (groupData.state) {
          const name = await getStateNameByCode(groupData.state);
          setStateName(name);
        }
      } catch (err) {
        setError("Failed to load group details");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, groupCode]);

  const loadMorePrograms = async () => {
    if (!group || loadingMore || !hasMore) return;
    
    try {
      setLoadingMore(true);
      const nextPage = currentPage + 1;
      const morePrograms = await fetchProgramsByGroupPaginated(group._id, nextPage, 6);
      
      setPrograms(prev => [...prev, ...(morePrograms.programs || [])]);
      setHasMore(morePrograms.hasMore || false);
      setCurrentPage(nextPage);
    } catch (err) {
      console.error('Error loading more programs:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const renderProgramsTab = (programs: any[], typeName: string, typeId?: string) => {
    if (programs.length === 0) {
      return (
        <Card className="text-center py-5 border-0 bg-light">
          <Card.Body>
            <i className="bi bi-collection display-1 text-muted mb-3"></i>
            <h5 className="text-muted">No {typeName.toLowerCase()} available</h5>
            <p className="text-muted">This group hasn't published any {typeName.toLowerCase()} yet.</p>
          </Card.Body>
        </Card>
      );
    }

    const isFestival = typeName.toLowerCase().includes('festival');

    return (
      <Row className="g-4">
        {programs.map((program) => (
          <Col key={program._id} lg={4} md={6}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Img 
                variant="top" 
                src={getPlayImageUrl(program.image1)} 
                style={{ height: "200px", objectFit: "cover" }}
                onError={(e) => handleImageError(e, 'PLAY')}
              />
              <Card.Body>
                <Card.Title className="h6">{program.title}</Card.Title>
                <Card.Text className="small text-muted">
                  {program.description?.substring(0, 100)}...
                </Card.Text>
                
                {/* Show festival plays if it's a festival */}
                {isFestival && festivalPlays[program._id] && (
                  <div className="mb-3">
                    <h6 className="small fw-bold text-primary mb-2">
                      <i className="bi bi-play-circle me-1"></i>
                      Scheduled Plays ({festivalPlays[program._id].length})
                    </h6>
                    {festivalPlays[program._id].length > 0 ? (
                      <div className="small">
                        {festivalPlays[program._id].slice(0, 3).map((fp: any, index: number) => (
                          <div key={fp._id} className="d-flex justify-content-between align-items-center mb-1 p-1 bg-light rounded">
                            <span className="text-truncate me-2">{fp.playId.title}</span>
                            <Badge bg="secondary" className="small">
                              {new Date(fp.scheduledDate).toLocaleDateString()}
                            </Badge>
                          </div>
                        ))}
                        {festivalPlays[program._id].length > 3 && (
                          <div className="text-muted small">+{festivalPlays[program._id].length - 3} more plays</div>
                        )}
                      </div>
                    ) : (
                      <div className="small text-muted">No plays scheduled yet</div>
                    )}
                  </div>
                )}
                
                <Link 
                  to={typeId === '6839c953077dbaaa4e2581da' ? `/play/${program._id}` : `/program/${program._id}`} 
                  className="btn btn-sm btn-warning"
                >
                  View Details
                </Link>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading group details...</p>
      </Container>
    );
  }

  if (error || !group) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Group Not Found</h4>
          <p>{error || "The requested group could not be found."}</p>
          <Link to="/groups" className="btn btn-warning">Back to Groups</Link>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Breadcrumb Navigation */}
      <Row className="mb-3">
        <Col xs={12}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/groups" className="text-decoration-none">Groups</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {group.name}
              </li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Hero Section */}
      <Row className="mb-5">
        <Col lg={4} className="mb-4">
          <Card className="border-0 shadow-lg">
            <div className="position-relative">
              <Card.Img 
                variant="top" 
                src={getGroupImageUrl(group.groupImage)} 
                style={{ height: "300px", objectFit: "cover" }}
                onError={(e) => handleImageError(e, 'GROUP')}
              />
              {group.isFeatured && (
                <Badge bg="warning" className="position-absolute top-0 end-0 m-3">
                  <i className="bi bi-star-fill me-1"></i>Featured
                </Badge>
              )}
            </div>
          </Card>
        </Col>
        <Col lg={8}>
          <div className="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h1 className="display-5 fw-bold text-dark mb-2">{group.name}</h1>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {group.specialties?.map((specialty, index) => (
                  <Badge key={index} bg="outline-warning" text="dark" className="px-3 py-2">
                    {specialty}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          
          <p className="lead text-muted mb-4">{group.description}</p>
          
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-calendar-event text-warning me-2"></i>
                <span><strong>Established:</strong> {group.foundedDate ? new Date(group.foundedDate).getFullYear() : group.establishedYear || "N/A"}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-people text-warning me-2"></i>
                <span><strong>Members:</strong> {group.totalMembers || "N/A"}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-geo-alt text-warning me-2"></i>
                <span><strong>Location:</strong> {stateName || group.state}, {group.country}</span>
              </div>
            </Col>
            <Col md={6}>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-envelope text-warning me-2"></i>
                <span><strong>Email:</strong> {group.email}</span>
              </div>
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-telephone text-warning me-2"></i>
                <span><strong>Phone:</strong> {group.phone}</span>
              </div>
              {group.website && (
                <div className="d-flex align-items-center mb-2">
                  <i className="bi bi-globe text-warning me-2"></i>
                  <a href={group.website} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    Visit Website
                  </a>
                </div>
              )}
            </Col>
          </Row>

          {/* Short URL Display */}
          <div className="mb-4">
            <div className="border rounded p-3 bg-light">
              <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-2">
                <div className="d-flex align-items-center flex-grow-1 min-w-0">
                  <i className="bi bi-link text-muted me-2"></i>
                  <span className="text-muted small me-2">Share:</span>
                  <code className="text-dark small text-break">{`${window.location.origin}/g/${group.groupCode}`}</code>
                </div>
                <Button 
                  variant="outline-secondary"
                  size="sm"
                  className="flex-shrink-0"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/g/${group.groupCode}`);
                  }}
                >
                  <i className="bi bi-clipboard"></i>
                </Button>
              </div>
            </div>
          </div>

          {/* Social Media Links and Share Button */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            {group.socialMedia && (
              <div className="d-flex gap-3">
                {group.socialMedia.facebook && (
                  <a href={group.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="text-primary">
                    <i className="bi bi-facebook" style={{ fontSize: "1.5rem" }}></i>
                  </a>
                )}
                {group.socialMedia.instagram && (
                  <a href={group.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="text-danger">
                    <i className="bi bi-instagram" style={{ fontSize: "1.5rem" }}></i>
                  </a>
                )}
                {group.socialMedia.twitter && (
                  <a href={group.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="text-info">
                    <i className="bi bi-twitter" style={{ fontSize: "1.5rem" }}></i>
                  </a>
                )}
                {group.socialMedia.youtube && (
                  <a href={group.socialMedia.youtube} target="_blank" rel="noopener noreferrer" className="text-danger">
                    <i className="bi bi-youtube" style={{ fontSize: "1.5rem" }}></i>
                  </a>
                )}
              </div>
            )}
            <EventShareButtons
              event={{
                title: group.name,
                date: `Established ${group.foundedDate ? new Date(group.foundedDate).getFullYear() : group.establishedYear || "N/A"}`,
                time: `${stateName || group.state}, ${group.country}`,
                venue: group.description || "Amazing theater group"
              }}
              bookingUrl={`${window.location.origin}/g/${group.groupCode}`}
              variant="outline-success"
              size="lg"
            />
          </div>
        </Col>
      </Row>

      {/* Awards Section */}
      {group.awards && group.awards.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <h3 className="fw-bold mb-4"><i className="bi bi-trophy text-warning me-2"></i>Awards & Recognition</h3>
            <Row className="g-3">
              {group.awards.map((award, index) => (
                <Col key={index} md={6} lg={4}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body>
                      <div className="d-flex align-items-center mb-2">
                        <i className="bi bi-award text-warning me-2"></i>
                        <Badge bg="warning" text="dark">{award.year}</Badge>
                      </div>
                      <Card.Title className="h6">{award.title}</Card.Title>
                      <Card.Text className="small text-muted">{award.description}</Card.Text>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {/* All Productions with Tabs */}
      <Row>
        <Col xs={12}>
          <div className="mb-4 d-flex justify-content-between align-items-center">
            <h3 className="fw-bold mb-0"><i className="bi bi-play-circle text-warning me-2"></i>All Productions</h3>
            {totalPrograms > 0 && (
              <Badge bg="secondary" className="fs-6">{totalPrograms} total</Badge>
            )}
          </div>
          
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || "plays")}
            className="mb-4"
          >
            {/* Theater Plays Tab (Default) */}
            <Tab 
              eventKey="plays" 
              title={
                <span>
                  <i className="bi bi-play-circle me-2"></i>
                  Plays
                  {programsByType['plays']?.length > 0 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      {programsByType['plays'].length}
                    </Badge>
                  )}
                </span>
              }
            >
              {renderProgramsTab(programsByType['plays'] || [], 'plays', '6839c953077dbaaa4e2581da')}
            </Tab>
            
            {/* Other Program Types Tabs */}
            {programTypes
              .filter(type => type._id !== '6839c953077dbaaa4e2581da')
              .map(type => {
                const typePrograms = programsByType[type._id] || [];
                if (typePrograms.length === 0) return null;
                
                return (
                  <Tab 
                    key={type._id}
                    eventKey={type._id} 
                    title={
                      <span>
                        <i className="bi bi-collection me-2"></i>
                        {type.name}
                        <Badge bg="warning" text="dark" className="ms-2">
                          {typePrograms.length}
                        </Badge>
                      </span>
                    }
                  >
                    {renderProgramsTab(typePrograms, type.name, type._id)}
                  </Tab>
                );
              })
            }
          </Tabs>
        </Col>
      </Row>
    </Container>
  );
};

export default GroupDetailPage;
